import axios from 'axios';
import { useGlobalContext } from 'GlobalContext';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import {
  createBulletin,
  deleteBulletin,
  getBulletins,
  resendBulletin,
  updateBulletin,
} from 'services/installation/bulletin';
import {
  BulletinResponse,
  CreateBulletinInput,
  ResendBulletinInput,
  UpdateBulletinInput,
} from 'services/interfaces/installation/bulletin';
import { useAppTabContext } from '../../AppContext';
import BulletinComponent from './Bulletin';

interface BulletinContextData {
  bulletins: BulletinResponse[];
  loading: boolean;
  isRegistered: boolean;
  formBulletin: BulletinResponse | null;
  isFormOpen: boolean;
  openCreateForm(): void;
  openEditForm(bulletin: BulletinResponse): void;
  closeForm(): void;
  isResendOpen: boolean;
  openResendForm(): void;
  closeResendForm(): void;
  handleCreate(payload: CreateBulletinInput): void;
  handleUpdate(bulletinId: number, payload: UpdateBulletinInput): void;
  handleDelete(bulletinId: number): void;
  handleResend(payload: ResendBulletinInput): void;
}

const Context = createContext<BulletinContextData>({} as BulletinContextData);

export const useBulletinContext = () => useContext(Context);

const BulletinContext = () => {
  const { app } = useAppTabContext();
  const { confirm } = useGlobalContext();

  const [loading, setLoading] = useState(true);
  const [isRegistered, setIsRegistered] = useState(true);
  const [bulletins, setBulletins] = useState<BulletinResponse[]>([]);
  const [formBulletin, setFormBulletin] = useState<BulletinResponse | null>(
    null,
  );
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isResendOpen, setIsResendOpen] = useState(false);

  const openCreateForm = () => {
    setFormBulletin(null);
    setIsFormOpen(true);
  };

  const openEditForm = (bulletin: BulletinResponse) => {
    setFormBulletin(bulletin);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setFormBulletin(null);
    setIsFormOpen(false);
  };

  const openResendForm = () => {
    setIsResendOpen(true);
  };

  const closeResendForm = () => {
    setIsResendOpen(false);
  };

  const loadBulletins = useCallback(() => {
    setLoading(true);
    getBulletins(app.idi)
      .then((res) => {
        setBulletins(res.data);
      })
      .catch((error) => {
        if (axios.isAxiosError(error) && error.response?.status !== 404) {
          console.error('Falha ao carregar boletins', error);
          toast.error('Falha ao carregar boletins');
        }

        if (error?.response?.status === 422) {
          setIsRegistered(false);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [app.idi]);

  const handleCreate = (payload: CreateBulletinInput) => {
    setLoading(true);
    createBulletin(payload)
      .then(() => {
        toast.success('Boletim criado com sucesso!');
        closeForm();
        loadBulletins();
      })
      .catch((error) => {
        console.error('Falha ao criar boletim', error);
        toast.error('Falha ao criar boletim!');
        setLoading(false);
      });
  };

  const handleUpdate = (bulletinId: number, payload: UpdateBulletinInput) => {
    setLoading(true);
    updateBulletin(app.idi, bulletinId, payload)
      .then(() => {
        toast.success('Boletim atualizado com sucesso!');
        closeForm();
        loadBulletins();
      })
      .catch((error) => {
        console.error('Falha ao atualizar boletim', error);
        toast.error('Falha ao atualizar boletim!');
        setLoading(false);
      });
  };

  const handleResend = (payload: ResendBulletinInput) => {
    setLoading(true);
    resendBulletin(app.idi, payload)
      .then((res) => {
        toast.success(res.data.message || 'Boletim reenviado com sucesso!');
        closeResendForm();
        setLoading(false);
      })
      .catch((error) => {
        console.error('Falha ao reenviar boletim', error);
        toast.error('Falha ao reenviar boletim!');
        setLoading(false);
      });
  };

  const handleDelete = (bulletinId: number) => {
    confirm('Excluir boletim', 'Deseja realmente excluir este boletim?', () => {
      setLoading(true);
      deleteBulletin(app.idi, bulletinId)
        .then(() => {
          toast.success('Boletim excluído com sucesso!');
          loadBulletins();
        })
        .catch((error) => {
          console.error('Falha ao excluir boletim', error);
          toast.error('Falha ao excluir boletim!');
          setLoading(false);
        });
    });
  };

  useEffect(() => {
    loadBulletins();
  }, [loadBulletins]);

  const data: BulletinContextData = {
    bulletins,
    loading,
    isRegistered,
    formBulletin,
    isFormOpen,
    openCreateForm,
    openEditForm,
    closeForm,
    isResendOpen,
    openResendForm,
    closeResendForm,
    handleCreate,
    handleUpdate,
    handleDelete,
    handleResend,
  };

  return (
    <Context.Provider value={data}>
      <BulletinComponent />
    </Context.Provider>
  );
};

export default BulletinContext;
